import { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import { Query, REFERENCE_MAX_AGE } from '../utils/queryCache';
import { useLoader } from './useLoader';

export const fetchTeacherProfileImage = async (
  teacherId: number,
  size: 'small' | 'large' = 'small',
  signal?: AbortSignal,
): Promise<string | undefined> => {
  try {
    const response = await api.get(`/teacher/${teacherId}/profile-image`, { params: { size }, signal });
    const { image, type } = response.data;
    if (image) return `data:${type};base64,${image}`;
    return undefined;
  } catch {
    return undefined;
  }
};

/**
 * Ritorna il data URL dell'immagine profilo di un singolo insegnante.
 * Se l'immagine non esiste o la chiamata fallisce, ritorna undefined.
 */
export const useTeacherProfileImage = (teacherId: number | undefined): string | undefined => {
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (teacherId === undefined) return;
    fetchTeacherProfileImage(teacherId).then(setImageUrl);
  }, [teacherId]);

  return imageUrl;
};

/**
 * Ritorna una mappa { [teacherId]: dataUrl } per una lista di ID insegnanti.
 * Utile nelle pagine che mostrano più insegnanti contemporaneamente.
 */
export const useTeacherProfileImages = (teacherIds: number[]): Record<number, string> => {
  const [imageMap, setImageMap] = useState<Record<number, string>>({});

  useEffect(() => {
    if (teacherIds.length === 0) return;

    const uniqueIds = Array.from(new Set(teacherIds));
    Promise.all(
      uniqueIds.map(async (id) => {
        const url = await fetchTeacherProfileImage(id);
        return { id, url };
      })
    ).then((results) => {
      const map: Record<number, string> = {};
      results.forEach(({ id, url }) => {
        if (url) map[id] = url;
      });
      setImageMap(map);
    });
  }, [teacherIds.join(',')]);

  return imageMap;
};

// Al massimo PHOTO_CONCURRENCY foto in download insieme: le foto non devono occupare tutte le
// connessioni prima degli elenchi e delle statistiche. Chi finisce passa il posto al primo in coda.
const PHOTO_CONCURRENCY = 3;
let activePhotos = 0;
const photoQueue: (() => void)[] = [];

const acquirePhotoSlot = (): Promise<void> => {
  if (activePhotos < PHOTO_CONCURRENCY) {
    activePhotos += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => photoQueue.push(resolve));
};

const releasePhotoSlot = () => {
  const next = photoQueue.shift();
  if (next) next();
  else activePhotos -= 1;
};

const loadPhoto = async (teacherId: number, signal: AbortSignal): Promise<string | undefined> => {
  await acquirePhotoSlot();
  try {
    if (signal.aborted) return undefined;
    return await fetchTeacherProfileImage(teacherId, 'small', signal);
  } finally {
    releasePhotoSlot();
  }
};

/**
 * Foto di un insegnante dagli elenchi (che ne riportano solo il nome del file in `image`).
 * Senza foto non parte nessuna richiesta. La foto resta in memoria: cambiando pagina non si
 * riscarica, e una foto nuova ha un nome nuovo, quindi si aggiorna da sola.
 * Se la richiesta fallisce ritorna undefined (restano le iniziali).
 */
export const useTeacherPhoto = ({ id, image }: { id: number; image?: string | null }): string | undefined => {
  const query = useMemo(
    (): Query<string | undefined> | null =>
      image
        ? { key: `teacher-photo:${id}:${image}`, load: (signal) => loadPhoto(id, signal), maxAge: REFERENCE_MAX_AGE }
        : null,
    [id, image],
  );
  return useLoader(query).data ?? undefined;
};
